import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';
import { useState, useEffect } from 'react';
import Backend from "../../Backend/Backend";
import Swal from 'sweetalert2';

export default function Contact() {

    const [backend, setBackend] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "Resume Optimization",
        message: ""
    });

    // ✅ Init backend
    useEffect(() => {
        const initBackend = async () => {
            const b = await new Backend().init();
            setBackend(b);
        };
        initBackend();
    }, []);

    // ✅ Common handler
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // ✅ Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!backend) return;

        setLoading(true);

        Swal.fire({
            title: 'Sending...',
            text: 'Please wait',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const res = await backend.contact(formData);

            Swal.close();

            if (res.error === "False") {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Message sent successfully 🚀',
                    timer: 2000,
                    showConfirmButton: false
                });

                setFormData({
                    name: "",
                    email: "",
                    subject: "Resume Optimization",
                    message: ""
                });

            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: res.message || 'Failed to send ❌',
                });
            }

        } catch (err) {
            console.error(err);
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Something went wrong ❌',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen bg-brand-light pt-20 md:pt-32 pb-12 md:pb-20 px-4 md:px-6 overflow-hidden">

            {/* BACKGROUND */}
            <div className="absolute inset-0 bg-brand-radial -z-10"></div>

            <div className="max-w-7xl mx-auto">

                {/* HEADER */}
                <div className="text-center mb-8 md:mb-16">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-3 md:mb-4 px-2">
                        Let’s Start a{" "}
                        <span className="bg-brand-gradient bg-clip-text text-transparent">
                            Conversation
                        </span>
                    </h1>

                    <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-4">
                        Have questions or need career advice? We're here to help you grow.
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-6 md:gap-8 lg:gap-12">

                    {/* LEFT SIDE - Contact Details */}
                    <div className="lg:col-span-1 space-y-6 md:space-y-8 order-2 lg:order-1">

                        <div className="bg-white/70 backdrop-blur-xl p-6 md:p-8 rounded-2xl md:rounded-3xl shadow-sm border border-white/40">
                            <h3 className="text-lg md:text-xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                                <MessageSquare className="text-brand-primary flex-shrink-0" size={20} />
                                <span>Contact Details</span>
                            </h3>

                            <div className="space-y-4 md:space-y-6">

                                <div className="flex items-start gap-3 md:gap-4">
                                    <Mail className="text-brand-primary flex-shrink-0 mt-1" size={18} />
                                    <p className="text-sm md:text-base break-all">info@geniusbridgetech.com</p>
                                </div>

                                <div className="flex items-start gap-3 md:gap-4">
                                    <Phone className="text-brand-accent flex-shrink-0 mt-1" size={18} />
                                    <p className="text-sm md:text-base">+1 3072074374</p>
                                </div>

                                <div className="flex items-start gap-3 md:gap-4">
                                    <MapPin className="text-brand-dark flex-shrink-0 mt-1" size={18} />
                                    <p className="text-sm md:text-base">30 N Gould St, Sheridan, WY</p>
                                </div>

                            </div>
                        </div>

                        <div className="bg-brand-gradient p-6 md:p-8 rounded-2xl md:rounded-3xl text-white shadow-xl">
                            <h4 className="font-bold text-base md:text-lg mb-2">Fast Response</h4>
                            <p className="text-white/80 text-sm md:text-base">
                                We respond within 24 hours during business days.
                            </p>
                        </div>

                    </div>

                    {/* FORM - Right Side */}
                    <div className="lg:col-span-2 order-1 lg:order-2">
                        <form onSubmit={handleSubmit} className="bg-white/70 backdrop-blur-xl p-5 md:p-8 lg:p-12 rounded-2xl md:rounded-3xl shadow-sm border border-white/40">

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">

                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Full Name"
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition text-sm md:text-base"
                                />

                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Email"
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition text-sm md:text-base"
                                />

                            </div>

                            <select
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition mb-4 md:mb-6 text-sm md:text-base appearance-none bg-white"
                            >
                                <option>Resume Optimization</option>
                                <option>Interview Training</option>
                                <option>Job Placement</option>
                                <option>General Inquiry</option>
                            </select>

                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="How can we help you?"
                                rows="5"
                                required
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition resize-y text-sm md:text-base mb-6 md:mb-8"
                            />

                            <button
                                disabled={loading}
                                className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-8 py-3 rounded-xl font-bold text-white transition
                                    ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-brand-gradient hover:opacity-90'}`}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send size={18} />
                                        Send Message
                                    </>
                                )}
                            </button>

                        </form>
                    </div>

                </div>
            </div>
        </div>
    );
}